import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, FlaskConical, BookOpen, Truck } from "lucide-react";
import { fetchProducts, fetchBundles, fetchArticles, fetchCategories, fetchReviews, fetchReviewsSummary } from "../lib/api";
import { HeroBackground } from "../components/HeroBackground";
import { ProductCard } from "../components/ProductCard";
import { BundleCard } from "../components/BundleCard";
import { ArticleCard } from "../components/ArticleCard";
import { ReviewCard } from "../components/ReviewCard";
import { StarRating } from "../components/StarRating";
import { Seo } from "../components/Seo";

const PILLARS = [
  { icon: FlaskConical, title: "Research Quality",
    body: "Every batch manufactured to exacting laboratory standards under strict cleanroom protocols." },
  { icon: ShieldCheck, title: "Exceptional Purity",
    body: "Rigorous QA. HPLC-verified ≥ 99%, mass-spec confirmed, endotoxin tested per lot." },
  { icon: BookOpen, title: "Knowledge Driven",
    body: "Extensive educational resources authored by researchers, for researchers." },
  { icon: Truck, title: "Fast Secure Shipping",
    body: "Cold-chain fulfilment, discreet packaging, tracked international dispatch." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ overall: { count: 0, avg: 0 } });

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
    fetchBundles().then(setBundles).catch(() => {});
    fetchArticles().then(setArticles).catch(() => {});
    fetchCategories().then(setCategories).catch(() => {});
    fetchReviews().then((r) => setReviews(r.slice(0, 3))).catch(() => {});
    fetchReviewsSummary().then(setReviewSummary).catch(() => {});
  }, []);

  const featured = products.filter((p) => !p.is_bundle).slice(0, 4);
  const featuredArticles = articles.slice(0, 3);
  const productLookup = React.useMemo(() => {
    const map = {};
    for (const p of products) map[p.slug] = p;
    return map;
  }, [products]);

  return (
    <div data-testid="home-page">
      <Seo description="Premium UK research peptides with ≥99% HPLC-verified purity. Buy BPC-157, NAD+, MOTS-c, Glutathione, Tesamorelin, Retatrutide and research bundles in GBP. For research use only." />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <HeroBackground />
        <div className="relative max-w-[1360px] mx-auto px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="reveal reveal-1 inline-flex items-center gap-3 px-3 py-1.5 rounded-full border border-[#2A2A2A] bg-[#111]/70">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1565FF] animate-pulse-slow" />
              <span className="label-mono text-white/80">Peptide Labs N.I. · Est. 2024</span>
            </div>
            <h1 className="reveal reveal-2 mt-8 text-[42px] sm:text-6xl lg:text-[80px] leading-[0.95] tracking-[-0.03em] font-medium text-white">
              Research Peptides
              <br />
              <span className="text-brushed">Without Compromise.</span>
            </h1>
            <p className="reveal reveal-3 mt-8 text-[15.5px] lg:text-[17px] max-w-xl text-[#B7BCC7] leading-relaxed">
              Premium laboratory research peptides manufactured to the highest standards
              with exceptional purity, documented consistency, and absolute transparency.
            </p>
            <div className="reveal reveal-4 mt-10 flex flex-wrap items-center gap-4">
              <Link to="/products" data-testid="hero-browse-products-btn" className="btn-primary inline-flex items-center gap-2">
                Browse Products <ArrowUpRight size={18} />
              </Link>
              <Link to="/knowledge" data-testid="hero-explore-kb-btn" className="btn-ghost inline-flex items-center gap-2">
                Explore Knowledge Base
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "≥ 99%", v: "HPLC Purity" },
                { k: "8", v: "Compounds" },
                { k: "24h", v: "Dispatch" },
              ].map((s, i) => (
                <div key={i} className="border-l border-[#2A2A2A] pl-4">
                  <div className="text-white text-2xl font-medium tracking-tight">{s.k}</div>
                  <div className="label-mono mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Vial visual on right */}
          <div className="lg:col-span-4 relative hidden lg:block">
            <div className="relative mx-auto w-[260px] h-[440px] rounded-t-[38px] rounded-b-[14px] vial-shell overflow-hidden shadow-lab-glow-lg">
              <div className="absolute top-0 left-0 right-0 h-[10%] bg-gradient-to-b from-[#2a2a2a] to-[#111] border-b border-[#2A2A2A]" />
              <div className="absolute top-[10%] left-0 right-0 h-[3%] bg-[#171717] border-b border-[#2A2A2A]" />
              <div className="absolute left-0 right-0 bottom-0 h-[62%] vial-liquid" />
              <div className="absolute left-1/2 -translate-x-1/2 top-[36%] w-[80%] px-3 py-3 bg-[#0B0B0B]/85 border border-[#2A2A2A] rounded-[8px]">
                <div className="label-mono text-[9px]">Peptide Labs N.I.</div>
                <div className="mt-2 text-white text-sm font-medium tracking-tight">BPC-157</div>
                <div className="mt-0.5 text-[#B7BCC7] text-[11px]">5 mg · Lyophilised</div>
                <div className="mt-3 h-px bg-[#2A2A2A]" />
                <div className="mt-2 flex justify-between text-[10px] text-[#6E7784] font-mono">
                  <span>LOT 24-P01</span><span>≥ 99.2%</span>
                </div>
              </div>
              <div className="absolute left-0 right-0 bottom-[60%] h-[2px] bg-[#1565FF]/70 blur-[2px]" />
            </div>
            <div className="absolute inset-0 -z-10 rounded-full bg-[#1565FF]/10 blur-3xl" />
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="relative py-24 border-t border-[#2A2A2A]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div>
              <div className="label-mono">01 · Catalog</div>
              <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight text-white font-medium max-w-xl">
                Featured research compounds.
              </h2>
            </div>
            <Link to="/products" className="btn-ghost inline-flex items-center gap-2" data-testid="home-view-all-products">
              View catalog <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE SHOWCASE */}
      {bundles.length > 0 && (
        <section className="relative py-24 bg-[#0e0e0e] border-t border-[#2A2A2A]" data-testid="home-bundle-section">
          <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
            <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
              <div>
                <div className="label-mono">02 · Curated bundles</div>
                <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight text-white font-medium max-w-2xl">
                  Save on multi-vial research kits.
                </h2>
                <p className="mt-4 text-[#B7BCC7] max-w-xl leading-relaxed">
                  Ship a full research kit in one cold-chain parcel with a batch-specific
                  certificate of analysis for every lot inside.
                </p>
              </div>
            </div>
            <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {bundles.map((b) => (
                <BundleCard key={b.id} bundle={b} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE */}
      <section className="relative py-24 bg-[#0e0e0e] border-t border-[#2A2A2A]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <div className="label-mono">03 · Standards</div>
            <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight text-white font-medium">
              Engineered for the researchers who care about the third decimal.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A] rounded-3xl overflow-hidden">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i} className="p-8 bg-[#0B0B0B] hover:bg-[#111] transition-colors">
                  <div className="w-11 h-11 rounded-xl bg-[#171717] border border-[#2A2A2A] grid place-items-center text-[#1565FF]">
                    <Icon size={20} />
                  </div>
                  <div className="mt-6 text-white text-lg font-medium tracking-tight">{p.title}</div>
                  <p className="mt-3 text-[#B7BCC7] text-[13.5px] leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="relative py-24 border-t border-[#2A2A2A]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div>
              <div className="label-mono">04 · Research Categories</div>
              <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight text-white font-medium max-w-xl">
                Research categories.
              </h2>
            </div>
            <Link to="/categories" className="btn-ghost inline-flex items-center gap-2" data-testid="home-view-categories">
              All categories <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2A2A] border border-[#2A2A2A] rounded-3xl overflow-hidden">
            {categories.map((c, i) => (
              <Link
                key={c.slug}
                to={`/products?category=${c.slug}`}
                data-testid={`home-category-${c.slug}`}
                className="p-6 lg:p-8 bg-[#0B0B0B] hover:bg-[#111] group transition-colors relative"
              >
                <div className="label-mono">0{i + 1}</div>
                <div className="mt-5 text-white text-lg lg:text-xl tracking-tight font-medium">{c.name}</div>
                <p className="mt-2 text-[#B7BCC7] text-[12.5px] leading-relaxed line-clamp-2">{c.description}</p>
                <ArrowUpRight size={16} className="absolute top-6 right-6 text-[#6E7784] group-hover:text-[#1565FF] transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="relative py-24 border-t border-[#2A2A2A]" data-testid="home-reviews-section">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="label-mono">05 · Reviews</div>
              <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight text-white font-medium max-w-2xl">
                Trusted by the researchers who care about the third decimal.
              </h2>
            </div>
            <div className="lg:col-span-4">
              <div className="p-6 border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e]">
                <div className="label-mono">Overall</div>
                <div className="mt-3 flex items-baseline gap-3">
                  <span className="text-white text-4xl font-medium font-mono tabular-nums">
                    {reviewSummary.overall.avg.toFixed(1)}
                  </span>
                  <span className="text-[#6E7784] font-mono">/ 5</span>
                </div>
                <div className="mt-2"><StarRating value={reviewSummary.overall.avg} size={14} /></div>
                <div className="mt-3 text-[11px] font-mono text-[#6E7784]">
                  {reviewSummary.overall.count} verified reviews
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((r) => (
              <ReviewCard key={r.id} review={r} showProductLink productLookup={productLookup} />
            ))}
          </div>
          <div className="mt-10">
            <Link to="/reviews" className="btn-ghost inline-flex items-center gap-2" data-testid="home-view-all-reviews">
              Read all reviews <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* KNOWLEDGE PREVIEW */}
      <section className="relative py-24 bg-[#0e0e0e] border-t border-[#2A2A2A]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
            <div>
              <div className="label-mono">06 · Knowledge Base</div>
              <h2 className="mt-4 text-3xl lg:text-5xl tracking-tight text-white font-medium max-w-xl">
                Research, not marketing.
              </h2>
            </div>
            <Link to="/knowledge" className="btn-ghost inline-flex items-center gap-2" data-testid="home-view-kb">
              All articles <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticles.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
      </section>

      {/* Compliance strip */}
      <section className="relative py-16 border-t border-[#2A2A2A]">
        <div className="max-w-[1360px] mx-auto px-6 lg:px-10">
          <div className="glass-panel rounded-3xl p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <div className="label-mono">Compliance</div>
              <div className="mt-3 text-white text-xl lg:text-2xl tracking-tight max-w-2xl font-medium">
                All products supplied are for in-vitro research use only and are not intended for human or animal consumption.
              </div>
            </div>
            <Link to="/about" className="btn-ghost inline-flex items-center gap-2" data-testid="home-compliance-cta">
              About the lab <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
